import { FC, useEffect, useRef } from "react";
import { Annotation, EditorState, Extension } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, placeholder } from "@codemirror/view";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentLess,
  insertTab,
} from "@codemirror/commands";
import { autocompletion, CompletionSource } from "@codemirror/autocomplete";
import { rqlHighlighting, rqlLanguage } from "../utils/rqlLanguage";
import {
  findVariableTokenAtPos,
  IKnownVariableNames,
  knownVariablesField,
  setKnownVariablesEffect,
  variableHighlightExtension,
} from "../utils/variableHighlightPlugin";
import {
  equationErrorField,
  equationErrorHoverTooltip,
  setEquationErrorsEffect,
} from "../utils/equationErrorHighlightPlugin";
import { IEquationErrorRange } from "../utils/equationErrorRanges";
import {
  RQL_RANK_OPTION_TYPE,
  RQL_VARIABLE_OPTION_TYPE,
} from "./equationCompletions";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusAdjacentOutsideEditor = (
  fromEl: HTMLElement,
  direction: "forward" | "backward",
): boolean => {
  const visible = Array.from(
    document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  const target =
    direction === "forward"
      ? visible.find(
          (el) => !!(fromEl.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
        )
      : [...visible]
          .reverse()
          .find(
            (el) => !!(fromEl.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING),
          );
  if (target) {
    target.focus();
    return true;
  }
  return false;
};

// Tags a dispatched transaction as "pushed in from outside the editor" (Beautify, Clear button)
// — the updateListener below checks for this annotation, NOT text equality, to decide whether to
// call onChange. This is deterministic even in the edge case where an external replacement
// happens to produce text identical to what's already there, unlike a plain
// doc.toString() === value comparison.
const externalEquationSync = Annotation.define<boolean>();

// `completionSources` is expected to be a STABLE array (built once by the caller via useMemo) —
// each source reads live data through its own closed-over ref/getter rather than through this
// prop changing identity, so the EditorView never needs to be torn down and rebuilt when the
// underlying question/response/rank lists change.
const EquationCodeMirrorEditor: FC<{
  value: string;
  // Identity of the document currently being edited (the rank tab name). A CHANGE to this prop
  // (as opposed to a change to `value` alone) means "switch to a different rank", which swaps in
  // that rank's own isolated EditorState — including its own undo/redo history — instead of
  // replacing the current document's text. Without this, every rank shared one continuous
  // history, so undoing on Rank B could walk backwards through Rank A's edits.
  stateKey: string;
  onChange: (text: string) => void;
  hasError: boolean;
  completionSources: CompletionSource[];
  knownVariables: IKnownVariableNames;
  onActiveVariablesChange: (variables: Set<string>) => void;
  onCursorEnterVariable: (variable: string) => void;
  // Character ranges to underline with the wavy error squiggle — computed by
  // RankingToolsEditor.tsx (equationErrorRanges.ts) alongside its existing content-error list.
  // Purely additive/cosmetic: hasError (the red border) and the error message list below the
  // editor are unaffected by this prop either way.
  errorRanges: IEquationErrorRange[];
  onPrettifyShortcut: () => void;
}> = ({
  value,
  stateKey,
  onChange,
  hasError,
  completionSources,
  knownVariables,
  onActiveVariablesChange,
  onCursorEnterVariable,
  errorRanges,
  onPrettifyShortcut,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onPrettifyShortcutRef = useRef(onPrettifyShortcut);
  onPrettifyShortcutRef.current = onPrettifyShortcut;
  const onActiveVariablesChangeRef = useRef(onActiveVariablesChange);
  onActiveVariablesChangeRef.current = onActiveVariablesChange;
  const onCursorEnterVariableRef = useRef(onCursorEnterVariable);
  onCursorEnterVariableRef.current = onCursorEnterVariable;
  const knownVariablesRef = useRef(knownVariables);
  knownVariablesRef.current = knownVariables;
  const errorRangesRef = useRef(errorRanges);
  errorRangesRef.current = errorRanges;
  // Cursor-position is the sole trigger for the persistent highlight (mouse-hover tracking was
  // tried and dropped — it required throttling posAtCoords/doc-rescan work on every mousemove,
  // which wasn't worth the complexity for this feature).
  const cursorVariableRef = useRef<string | null>(null);
  const notifyActiveVariables = () => {
    onActiveVariablesChangeRef.current(
      cursorVariableRef.current
        ? new Set([cursorVariableRef.current])
        : new Set(),
    );
  };

  // One EditorState per rank, keyed by stateKey, so each rank's undo/redo history (part of the
  // history() extension's own state field) stays fully isolated from every other rank's. Swapped
  // into the single long-lived EditorView via view.setState() on a stateKey change, rather than
  // replacing the document's text in place.
  const statesByKeyRef = useRef<Map<string, EditorState>>(new Map());
  const activeKeyRef = useRef<string | null>(null);

  const buildExtensions = (): Extension[] => [
    history(),
    keymap.of([
      ...defaultKeymap,
      ...historyKeymap,
      { key: "Tab", run: insertTab, shift: indentLess },
      {
        key: "Escape",
        run: (view) => {
          if (!focusAdjacentOutsideEditor(view.contentDOM, "forward")) {
            view.contentDOM.blur();
          }
          return true;
        },
        shift: (view) => {
          if (!focusAdjacentOutsideEditor(view.contentDOM, "backward")) {
            view.contentDOM.blur();
          }
          return true;
        },
      },
      {
        key: "Mod-Shift-p",
        run: () => {
          onPrettifyShortcutRef.current();
          return true;
        },
        preventDefault: true,
      },
    ]),
    rqlLanguage,
    rqlHighlighting,
    variableHighlightExtension,
    equationErrorField,
    equationErrorHoverTooltip,
    lineNumbers(),
    EditorView.baseTheme({
      '.cm-gutters': {
        backgroundColor: '#ebebeb',
        border: 'none',
        color: 'var(--mnq-accessibility-bg)',
      },
      '.cm-lineNumbers .cm-gutterElement': {
        padding: '0 8px 0 5px',
      },
    }),
    autocompletion({
      override: completionSources,
      icons: false,
      // The untriggered generic source (equationCompletions.ts's makeGenericSource) can
      // re-filter across every operator/question/response/rank list on nearly every
      // keystroke once its minWordLength is low — bumping this above CodeMirror's 100ms
      // default gives typing a moment to pause before re-querying, without making the
      // dedicated @/#/:/$ triggers feel noticeably delayed.
      activateOnTypingDelay: 200,
      optionClass: (completion) =>
        completion.type === RQL_VARIABLE_OPTION_TYPE ||
        completion.type === RQL_RANK_OPTION_TYPE
          ? "entryNameOption"
          : "",
    }),
    EditorView.lineWrapping,
    EditorView.contentAttributes.of({
      "data-gramm": "false",
      "data-gramm_editor": "true",
      "data-enable-grammarly": "false",
    }),
    placeholder("Start build equation"),
    EditorView.updateListener.of((update) => {
      if (
        update.docChanged &&
        !update.transactions.some((tr) => tr.annotation(externalEquationSync))
      ) {
        onChangeRef.current(update.state.doc.toString());
      }
      // Cursor-position half of the persistent highlight (see notifyActiveVariables above) —
      // re-derived on every doc/selection change so it stays correct as the user types or
      // clicks around, inclusive of the cursor landing exactly at a variable's end right after
      // picking it from autocomplete. Entering a (different) variable also scrolls the Question
      // Snapshot panel to it — same as picking one from the autocomplete dropdown — so clicking
      // an existing variable in the equation reveals its row even if it's currently scrolled out
      // of view, not just highlights it invisibly.
      if (update.docChanged || update.selectionSet) {
        const nextCursorVariable = findVariableTokenAtPos(
          update.state.doc.toString(),
          update.state.field(knownVariablesField),
          update.state.selection.main.head,
        );
        if (nextCursorVariable !== cursorVariableRef.current) {
          cursorVariableRef.current = nextCursorVariable;
          notifyActiveVariables();
          if (nextCursorVariable) {
            onCursorEnterVariableRef.current(nextCursorVariable);
          }
        }
      }
    }),
  ];

  // Builds a fresh EditorState for `key`/`doc`, seeded with the latest known-variable set so its
  // decoration layer isn't blank until the next knownVariables prop change comes in.
  const createStateFor = (doc: string): EditorState => {
    const initial = EditorState.create({ doc, extensions: buildExtensions() });
    return initial.update({
      effects: [
        setKnownVariablesEffect.of(knownVariablesRef.current),
        setEquationErrorsEffect.of(errorRangesRef.current),
      ],
    }).state;
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const initialState = createStateFor(value);
    statesByKeyRef.current.set(stateKey, initialState);
    activeKeyRef.current = stateKey;
    const view = new EditorView({ state: initialState, parent: containerRef.current });
    viewRef.current = view;

    // Re-derive the persistent highlight from THIS state's actual cursor position (start of
    // document on a fresh mount) rather than leaving whatever activeVariables happened to be
    // left over in the store from before this component last unmounted (e.g. navigating away
    // from the Equation Editor and back) — that stale value has no relation to where the cursor
    // actually is in this brand-new EditorState.
    cursorVariableRef.current = findVariableTokenAtPos(
      initialState.doc.toString(),
      initialState.field(knownVariablesField),
      initialState.selection.main.head,
    );
    notifyActiveVariables();

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Mount once — completionSources is stable (see prop comment above); `value`/`stateKey`
    // changes after mount are handled by the effect below via setState/dispatch, not by
    // recreating the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles BOTH: (a) switching ranks (stateKey changed) — swap in that rank's own EditorState,
  // creating one on first visit, after stashing the outgoing rank's latest state back into the
  // map so its history/selection survive the switch; and (b) an external same-rank replacement
  // (Beautify, Clear) — a normal replace transaction on the current state, preserving its undo
  // history so the formatting/clear operation itself remains undoable.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    if (activeKeyRef.current !== stateKey) {
      if (activeKeyRef.current !== null) {
        statesByKeyRef.current.set(activeKeyRef.current, view.state);
      }
      let nextState = statesByKeyRef.current.get(stateKey);
      if (!nextState || nextState.doc.toString() !== value) {
        nextState = createStateFor(value);
      }
      view.setState(nextState);
      statesByKeyRef.current.set(stateKey, nextState);
      activeKeyRef.current = stateKey;

      const nextCursorVariable = findVariableTokenAtPos(
        nextState.doc.toString(),
        nextState.field(knownVariablesField),
        nextState.selection.main.head,
      );
      cursorVariableRef.current = nextCursorVariable;
      notifyActiveVariables();
      return;
    }

    if (view.state.doc.toString() !== value) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
        annotations: externalEquationSync.of(true),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, stateKey]);

  // Pushes the live question/response variable sets into the ACTIVE editor's state (see
  // variableHighlightPlugin.ts) whenever they change upstream, without tearing down the
  // EditorView. `knownVariables` is expected to be a fresh object each time the underlying
  // `questions` data changes (not mutated in place), so this effect fires exactly then. Inactive
  // ranks' stashed states pick up the latest value lazily, via knownVariablesRef, the next time
  // they're switched into.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({ effects: setKnownVariablesEffect.of(knownVariables) });
  }, [knownVariables]);

  // Pushes the latest content-error ranges into the ACTIVE editor's state, same pattern as
  // knownVariables above — recomputed by RankingToolsEditor alongside its existing validation
  // effect, so this fires whenever the equation, question data, or rank changes.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({ effects: setEquationErrorsEffect.of(errorRanges) });
  }, [errorRanges]);

  return (
    <div
      ref={containerRef}
      className={`rankingCodeMirror${hasError ? " hasError" : ""}`}
    />
  );
};

export default EquationCodeMirrorEditor;
